import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { FullReservationData } from '../App';
import { ShieldCheckIcon, CreditCardIcon, PackageIcon, TagIcon, LockIcon } from 'lucide-react';
interface CheckoutPageProps {
  onNavigate: (page: string) => void;
  reservationData: FullReservationData | null;
}
export function CheckoutPage({
  onNavigate,
  reservationData
}: CheckoutPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [billingData, setBillingData] = useState({
    firstName: reservationData?.firstName || '',
    lastName: reservationData?.lastName || '',
    company: '',
    country: 'United States (US)',
    street1: '',
    street2: '',
    city: '',
    state: 'Maryland',
    zip: '',
    phone: reservationData?.phoneNumber || '',
    email: reservationData?.email || '',
    notes: ''
  });
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiry: '',
    cvc: ''
  });
  if (!reservationData) {
    return <main className="flex-grow w-full bg-brand-gray min-h-screen flex flex-col" data-id="element-836">
        <PageHeader title="Checkout" breadcrumb="Checkout" onNavigateHome={() => onNavigate('home')} data-id="element-837" />
        <div className="flex-grow flex items-center justify-center p-8" data-id="element-838">
          <div className="text-center bg-white p-12 rounded-3xl shadow-xl max-w-md w-full" data-id="element-839">
            <h2 className="text-2xl font-bold text-brand-navy mb-4" data-id="element-840">
              No Order Found
            </h2>
            <p className="text-brand-charcoal/60 mb-8" data-id="element-841">
              Please start a reservation to proceed to checkout.
            </p>
            <button onClick={() => onNavigate('home')} className="w-full py-4 bg-brand-red text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-lg" data-id="element-842">
              Return Home
            </button>
          </div>
        </div>
      </main>;
  }
  // Calculate prices (same logic as Cart)
  const basePrice = parseFloat(reservationData.price.replace(/[^0-9.]/g, '')) || 150.0;
  const quantity = 1; // Assuming 1 for simplicity based on cart
  const subtotal = basePrice * quantity;
  const insuranceFee = 10.0;
  let gelCushionPrice = 0;
  if (reservationData.gelCushion && reservationData.gelCushion !== 'None') {
    gelCushionPrice = reservationData.gelCushion.includes('Premium') ? 15.99 : 5.99;
  }
  const taxRate = 0.06;
  const tax = (subtotal + insuranceFee + gelCushionPrice) * taxRate;
  const total = subtotal + insuranceFee + gelCushionPrice + tax;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setBillingData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Order placed successfully! Thank you for choosing WheelAround Rentals.');
    onNavigate('home');
  };
  const inputClass = 'w-full bg-white border border-gray-200 text-brand-navy placeholder-gray-400 rounded-xl py-3 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none text-sm';
  const labelClass = 'block text-brand-charcoal/80 text-sm font-semibold mb-2';
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-843">
      <PageHeader title="Checkout" breadcrumb="Checkout" onNavigateHome={() => onNavigate('home')} data-id="element-844" />

      <section className="py-12 md:py-20" data-id="element-845">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-846">
          {/* Coupon Banner */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-8 flex items-center gap-3" data-id="element-847">
            <TagIcon className="w-5 h-5 text-brand-red" data-id="element-848" />
            <p className="text-brand-navy font-medium text-sm" data-id="element-849">
              Have A Coupon?{' '}
              <button className="text-brand-red hover:underline font-semibold" data-id="element-850">
                Click here to enter your code
              </button>
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row gap-8 items-start" data-id="element-851">
            {/* Left Column: Billing Details */}
            <div className="w-full lg:w-[60%] space-y-8" data-id="element-852">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-853">
                <h2 className="text-2xl font-bold text-brand-navy mb-6 pb-4 border-b border-gray-100" data-id="element-854">
                  Billing Details
                </h2>

                <div className="space-y-5" data-id="element-855">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-856">
                    <div data-id="element-857">
                      <label htmlFor="firstName" className={labelClass} data-id="element-858">
                        First name <span className="text-brand-red" data-id="element-859">*</span>
                      </label>
                      <input type="text" id="firstName" name="firstName" value={billingData.firstName} onChange={handleInputChange} required className={inputClass} data-id="element-860" />
                    </div>
                    <div data-id="element-861">
                      <label htmlFor="lastName" className={labelClass} data-id="element-862">
                        Last name <span className="text-brand-red" data-id="element-863">*</span>
                      </label>
                      <input type="text" id="lastName" name="lastName" value={billingData.lastName} onChange={handleInputChange} required className={inputClass} data-id="element-864" />
                    </div>
                  </div>

                  <div data-id="element-865">
                    <label htmlFor="company" className={labelClass} data-id="element-866">
                      Company name (optional)
                    </label>
                    <input type="text" id="company" name="company" value={billingData.company} onChange={handleInputChange} className={inputClass} data-id="element-867" />
                  </div>

                  <div data-id="element-868">
                    <label htmlFor="country" className={labelClass} data-id="element-869">
                      Country / Region <span className="text-brand-red" data-id="element-870">*</span>
                    </label>
                    <select id="country" name="country" value={billingData.country} onChange={handleInputChange} required className={inputClass} data-id="element-871">
                      <option value="United States (US)" data-id="element-872">
                        United States (US)
                      </option>
                      <option value="Canada" data-id="element-873">Canada</option>
                    </select>
                  </div>

                  <div data-id="element-874">
                    <label htmlFor="street1" className={labelClass} data-id="element-875">
                      Street address <span className="text-brand-red" data-id="element-876">*</span>
                    </label>
                    <input type="text" id="street1" name="street1" value={billingData.street1} onChange={handleInputChange} placeholder="House number and street name" required className={`${inputClass} mb-3`} data-id="element-877" />
                    <input type="text" id="street2" name="street2" value={billingData.street2} onChange={handleInputChange} placeholder="Apartment, suite, unit, etc. (optional)" className={inputClass} data-id="element-878" />
                  </div>

                  <div data-id="element-879">
                    <label htmlFor="city" className={labelClass} data-id="element-880">
                      Town / City <span className="text-brand-red" data-id="element-881">*</span>
                    </label>
                    <input type="text" id="city" name="city" value={billingData.city} onChange={handleInputChange} required className={inputClass} data-id="element-882" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-883">
                    <div data-id="element-884">
                      <label htmlFor="state" className={labelClass} data-id="element-885">
                        State <span className="text-brand-red" data-id="element-886">*</span>
                      </label>
                      <select id="state" name="state" value={billingData.state} onChange={handleInputChange} required className={inputClass} data-id="element-887">
                        <option value="Maryland" data-id="element-888">Maryland</option>
                        <option value="Virginia" data-id="element-889">Virginia</option>
                        <option value="Washington DC" data-id="element-890">Washington DC</option>
                      </select>
                    </div>
                    <div data-id="element-891">
                      <label htmlFor="zip" className={labelClass} data-id="element-892">
                        ZIP Code <span className="text-brand-red" data-id="element-893">*</span>
                      </label>
                      <input type="text" id="zip" name="zip" value={billingData.zip} onChange={handleInputChange} required className={inputClass} data-id="element-894" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-895">
                    <div data-id="element-896">
                      <label htmlFor="phone" className={labelClass} data-id="element-897">
                        Phone <span className="text-brand-red" data-id="element-898">*</span>
                      </label>
                      <input type="tel" id="phone" name="phone" value={billingData.phone} onChange={handleInputChange} required className={inputClass} data-id="element-899" />
                    </div>
                    <div data-id="element-900">
                      <label htmlFor="email" className={labelClass} data-id="element-901">
                        Email address <span className="text-brand-red" data-id="element-902">*</span>
                      </label>
                      <input type="email" id="email" name="email" value={billingData.email} onChange={handleInputChange} required className={inputClass} data-id="element-903" />
                    </div>
                  </div>
                </div>

                <div className="mt-10" data-id="element-904">
                  <h3 className="text-xl font-bold text-brand-navy mb-4" data-id="element-905">
                    Additional Information
                  </h3>
                  <div data-id="element-906">
                    <label htmlFor="notes" className={labelClass} data-id="element-907">
                      Order notes (optional)
                    </label>
                    <textarea id="notes" name="notes" value={billingData.notes} onChange={handleInputChange} placeholder="Notes about your order, e.g. special notes for delivery." rows={4} className={`${inputClass} resize-none`} data-id="element-908" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Order & Payment */}
            <div className="w-full lg:w-[40%] space-y-8" data-id="element-909">
              {/* Your Order Summary */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-910">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50" data-id="element-911">
                  <h3 className="text-xl font-bold text-brand-navy" data-id="element-912">
                    Your Order
                  </h3>
                </div>

                <div className="p-6" data-id="element-913">
                  {/* Product Row */}
                  <div className="flex justify-between items-start pb-4 border-b border-gray-100 mb-4" data-id="element-914">
                    <div className="flex gap-3" data-id="element-915">
                      <div className="w-12 h-12 bg-brand-gray rounded-lg flex items-center justify-center shrink-0 border border-gray-100" data-id="element-916">
                        <PackageIcon className="w-6 h-6 text-brand-navy/40" data-id="element-917" />
                      </div>
                      <div data-id="element-918">
                        <p className="font-bold text-brand-navy text-sm capitalize" data-id="element-919">
                          {reservationData.equipment}
                        </p>
                        <p className="text-brand-charcoal/50 text-xs" data-id="element-920">
                          {reservationData.email}
                        </p>
                        {gelCushionPrice > 0 && <p className="text-brand-charcoal/60 text-xs mt-1" data-id="element-921">
                            Gel Cushion (x1):{' '}
                            <span className="font-semibold text-brand-navy" data-id="element-922">
                              {formatCurrency(gelCushionPrice)}
                            </span>
                          </p>}
                      </div>
                    </div>
                    <div className="font-bold text-brand-navy text-sm" data-id="element-923">
                      {formatCurrency(basePrice)}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="space-y-3 text-sm" data-id="element-924">
                    <div className="flex justify-between" data-id="element-925">
                      <span className="text-brand-charcoal/60" data-id="element-926">Subtotal</span>
                      <span className="font-bold text-brand-navy" data-id="element-927">
                        {formatCurrency(subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-928">
                      <span className="text-brand-charcoal/60" data-id="element-929">
                        Total Insurance Fee (x1)
                      </span>
                      <span className="font-bold text-brand-navy" data-id="element-930">
                        {formatCurrency(insuranceFee)}
                      </span>
                    </div>
                    {gelCushionPrice > 0 && <div className="flex justify-between" data-id="element-931">
                        <span className="text-brand-charcoal/60" data-id="element-932">
                          Gel Cushion
                        </span>
                        <span className="font-bold text-brand-navy" data-id="element-933">
                          {formatCurrency(gelCushionPrice)}
                        </span>
                      </div>}
                    <div className="flex justify-between pb-4 border-b border-gray-100" data-id="element-934">
                      <span className="text-brand-charcoal/60" data-id="element-935">Tax</span>
                      <span className="font-bold text-brand-navy" data-id="element-936">
                        {formatCurrency(tax)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2" data-id="element-937">
                      <span className="font-bold text-brand-navy text-base" data-id="element-938">
                        Total
                      </span>
                      <span className="font-extrabold text-brand-red text-lg" data-id="element-939">
                        {formatCurrency(total)}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Payment Method */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-940">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50" data-id="element-941">
                  <h3 className="text-xl font-bold text-brand-navy" data-id="element-942">
                    Payment Method
                  </h3>
                </div>

                <div className="p-6" data-id="element-943">
                  <div className="bg-brand-gray/50 border border-gray-200 rounded-xl p-5 mb-6" data-id="element-944">
                    <div className="flex items-center gap-3 mb-4" data-id="element-945">
                      <input type="radio" checked readOnly className="w-4 h-4 text-brand-red focus:ring-brand-red" data-id="element-946" />
                      <span className="font-bold text-brand-navy text-sm" data-id="element-947">
                        Credit Card
                      </span>
                      <div className="flex gap-1 ml-auto" data-id="element-948">
                        <div className="w-8 h-5 bg-blue-600 rounded text-[8px] text-white font-bold flex items-center justify-center" data-id="element-949">
                          VISA
                        </div>
                        <div className="w-8 h-5 bg-orange-500 rounded text-[8px] text-white font-bold flex items-center justify-center" data-id="element-950">
                          MC
                        </div>
                        <div className="w-8 h-5 bg-blue-400 rounded text-[8px] text-white font-bold flex items-center justify-center" data-id="element-951">
                          AMEX
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-brand-charcoal/60 mb-4 bg-white p-3 rounded-lg border border-gray-100" data-id="element-952">
                      Pay securely using your credit card.
                    </p>

                    <div className="space-y-4" data-id="element-953">
                      <div data-id="element-954">
                        <label className="block text-brand-charcoal/80 text-xs font-semibold mb-1" data-id="element-955">
                          Card Number <span className="text-brand-red" data-id="element-956">*</span>
                        </label>
                        <div className="relative" data-id="element-957">
                          <input type="text" name="cardNumber" value={paymentData.cardNumber} onChange={handlePaymentChange} placeholder="•••• •••• •••• ••••" required className={`${inputClass} pl-10`} data-id="element-958" />
                          <CreditCardIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-959" />
                        </div>
                      </div>
                      <div className="flex gap-4" data-id="element-960">
                        <div className="flex-1" data-id="element-961">
                          <label className="block text-brand-charcoal/80 text-xs font-semibold mb-1" data-id="element-962">
                            Expiration (MM/YY){' '}
                            <span className="text-brand-red" data-id="element-963">*</span>
                          </label>
                          <input type="text" name="expiry" value={paymentData.expiry} onChange={handlePaymentChange} placeholder="MM / YY" required className={inputClass} data-id="element-964" />
                        </div>
                        <div className="flex-1" data-id="element-965">
                          <label className="block text-brand-charcoal/80 text-xs font-semibold mb-1" data-id="element-966">
                            Card Security Code{' '}
                            <span className="text-brand-red" data-id="element-967">*</span>
                          </label>
                          <input type="text" name="cvc" value={paymentData.cvc} onChange={handlePaymentChange} placeholder="CVC" required className={inputClass} data-id="element-968" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-brand-charcoal/60 leading-relaxed mb-6" data-id="element-969">
                    Your personal data will be used to process your order,
                    support your experience throughout this website, and for
                    other purposes described in our{' '}
                    <a href="#" className="text-brand-red hover:underline" data-id="element-970">
                      privacy policy
                    </a>
                    .
                  </p>

                  <button type="submit" className="w-full py-4 bg-brand-red text-white rounded-xl font-bold text-lg hover:bg-red-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-971">
                    <LockIcon className="w-4 h-4" data-id="element-972" />
                    Place Order
                  </button>
                </div>
              </motion.div>
            </div>
          </form>
        </div>
      </section>
    </main>;
}